import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { recordLogin } from '../services/activityLog';
import { VIEW_AS_EMAILS, withRealTokens } from '../config/viewAs';
import { auth, database, firebaseConfig } from '../firebaseConfig';
import { sendWelcomeInvite } from '../services/welcomeEmail';
import { initializeApp, getApps } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
  getAuth
} from 'firebase/auth';
import { ref, get, set } from 'firebase/database';
import { normalizeRole } from '../config/roles';

// Every new account starts on this temporary password with users/<uid>/mustChangePassword = true;
// ChangePasswordGate makes them pick their own on first sign-in.
export const TEMP_PASSWORD = 'welcome';

// Separate Firebase app so creating a user does not sign the admin out
const getSecondaryAuth = () => {
  const secondaryApp =
    getApps().find(a => a.name === 'secondary') || initializeApp(firebaseConfig, 'secondary');
  return getAuth(secondaryApp);
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  // The user Kelly is previewing as: { uid, displayName, email, role }. Null means her own account.
  const [viewAsUser, setViewAsUser] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [profile, setProfile] = useState({ phone: '', photoUrl: '', contactEmail: '' });
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [error, setError] = useState(null);

  // Monitor auth state
  useEffect(() => {
    console.log('[AUTH] Setting up auth state listener');

    let unsubscribe;

    // Set persistence BEFORE setting up the listener
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log('[AUTH] Persistence configured successfully');
        unsubscribe = setupAuthListener();
      })
      .catch((err) => {
        console.error('[AUTH] Error setting persistence:', err);
        // Still set up listener even if persistence fails
        unsubscribe = setupAuthListener();
      });

    const setupAuthListener = () => {
      return onAuthStateChanged(auth, async (currentUser) => {
        try {
          console.log('[AUTH] Auth state changed:', currentUser ? currentUser.email : 'null');
          if (currentUser) {
            console.log('[AUTH] User authenticated, UID:', currentUser.uid);
            recordLogin(currentUser.uid);
            setUser(currentUser);
            // Fetch user role and displayName from database
            try {
              const userRef = ref(database, `users/${currentUser.uid}`);
              const snapshot = await get(userRef);
              if (snapshot.exists()) {
                console.log('[AUTH] User data found in database');
                setUserRole(normalizeRole(snapshot.val().role));
                setMustChangePassword(snapshot.val().mustChangePassword === true);
                setDisplayName(snapshot.val().displayName);
                setProfile({ phone: snapshot.val().profile?.phone || '', photoUrl: snapshot.val().profile?.profilePhotoUrl || '', contactEmail: snapshot.val().profile?.contactEmail || '' });
              } else {
                console.log('[AUTH] No user data found in database for UID:', currentUser.uid);
              }
            } catch (dbError) {
              console.error('[AUTH] Error fetching user data from database:', dbError);
              // Still keep user logged in even if database read fails
              setUser(currentUser);
            }
          } else {
            console.log('[AUTH] User not authenticated');
            setUser(null);
            setUserRole(null);
            setMustChangePassword(false);
            setDisplayName(null);
            setProfile({ phone: '', photoUrl: '', contactEmail: '' });
          }
        } catch (err) {
          console.error('[AUTH] Auth state change error:', err);
        } finally {
          console.log('[AUTH] Setting loading to false');
          setLoading(false);
        }
      });
    };

    // Handle Back-Forward Cache restoration
    const handlePageShow = (event) => {
      if (event.persisted) {
        console.log('[AUTH] Page restored from Back-Forward Cache');
        // Reinitialize auth listener after page restoration
        if (unsubscribe) {
          unsubscribe();
        }
        unsubscribe = setupAuthListener();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    // Return cleanup function
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signup = async (email, displayName, role, organization = 'RESIDENCE', { sendWelcome = true } = {}) => {
    try {
      setError(null);
      const secondaryAuth = getSecondaryAuth();
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        email,
        TEMP_PASSWORD
      );
      const newUser = userCredential.user;

      // Store user info in database (admin is still signed in on the primary app)
      await set(ref(database, `users/${newUser.uid}`), {
        email,
        displayName,
        role,
        organization,
        mustChangePassword: true,
        createdAt: new Date().toISOString()
      });

      await signOut(secondaryAuth);

      // Invite: ONE email. The branded SendGrid welcome (with the set-password link) when SendGrid is set
      // up; otherwise Firebase's own set-password email. See services/welcomeEmail.js.
      // Skipped when the admin leaves "Send welcome email now" unchecked; Users > Send welcome email sends it later.
      if (sendWelcome) {
        const invite = await sendWelcomeInvite(newUser.uid, email, displayName);
        newUser.inviteSent = invite.ok;
        newUser.inviteVia = invite.via;
      } else {
        newUser.inviteSkipped = true;
      }

      return newUser;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setUser(null);
      setUserRole(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const canViewAs = userRole === 'Admin' || VIEW_AS_EMAILS.includes((user?.email || '').toLowerCase());

  // While previewing a person, pages read that person's uid, role and name. Preview only: writes made in this mode are saved to that person's data.
  const viewingUser = canViewAs && viewAsUser ? viewAsUser : null;
  // Memoized: a new object on every render made pages that reload on [user] (a transaction, the task list)
  // reload again and again while previewing someone, so the page kept flashing back to Loading.
  const vuUid = viewingUser?.uid; const vuEmail = viewingUser?.email; const vuName = viewingUser?.displayName;
  const effectiveUser = useMemo(() => (vuUid && user
    ? withRealTokens(user, { uid: vuUid, email: vuEmail || user.email, displayName: vuName })
    : user), [user, vuUid, vuEmail, vuName]);

  // When previewing someone, load that person's contact details and photo, not Kelly's.
  const [viewProfile, setViewProfile] = useState(null);
  const viewingUid = viewingUser?.uid || null;
  useEffect(() => {
    if (!viewingUid) { setViewProfile(null); return; }
    get(ref(database, `users/${viewingUid}`))
      .then((snap) => {
        const d = snap.val() || {};
        setViewProfile({ phone: d.profile?.phone || '', photoUrl: d.profile?.profilePhotoUrl || '', contactEmail: d.profile?.contactEmail || d.email || '' });
      })
      .catch(() => setViewProfile({ phone: '', photoUrl: '', contactEmail: '' }));
  }, [viewingUid]);

  const value = {
    user: effectiveUser,
    realUser: user,
    userRole: viewingUser ? normalizeRole(viewingUser.role || 'Agent') : userRole,
    realRole: userRole,
    viewAsUser: viewingUser,
    setViewAsUser,
    canViewAs,
    displayName: viewingUser ? viewingUser.displayName : displayName,
    profile: viewingUser ? (viewProfile || { phone: '', photoUrl: '', contactEmail: '' }) : profile,
    loading,
    error,
    // Real signed-in user only (not view-as): true until they replace the temporary password.
    mustChangePassword: !!user && mustChangePassword,
    clearMustChangePassword: () => setMustChangePassword(false),
    login,
    signup,
    resetPassword,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
